import React, { ChangeEvent, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import messages from './messages';
import SelectInput from '../../common/Select';
import InputText from '../../common/InputText';
import styles from './AddressForm.module.scss';
import statesOptions from './states.json';
import SearchLocationInput from '../../SearchLocationInput';
import { formatPhone } from '../../../utils';
import { AddressFormProps, AddressMapKeyType, AddressMapType } from './types';
import { AddressComponents } from '../../SearchLocationInput/types';
import ShopPayEmailField from '../../ShopPayEmailField';

const LOCATION_ADDRESS_MAP: AddressMapType = {
  administrative_area_level_1: 'state',
  country: 'country',
  locality: 'city',
  postal_code: 'postalCode',
  street_number: 'streetNumber',
  route: 'route'
};

const AddressForm = ({
  prefix,
  title,
  handleChange,
  handleBlur,
  isShowingContactDetails,
  isAddressAutocomplete,
  onAddressAutocomplete,
  onFieldValueSet,
  values,
  touched,
  errors,
  inputType,
}: AddressFormProps ) => {
  const { formatMessage } = useIntl();
  const additionalStreetAddressRef = useRef<HTMLInputElement>(null);
  const phoneNumberRef = useRef<HTMLInputElement>(null);

  const showShopPayEmailCapture = window?.purTogglesEnabled(
    'shop_pay_email_field',
  );

  const isMobile = window.innerWidth <= 768;

  const shopPayEmailFieldsPortalElementId = isMobile
    ? 'shop-pay-email-field-container-mobile'
    : 'shop-pay-email-field-container-desktop';

  const formatPhoneNumber = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const formattedValue = formatPhone(event?.target?.value || '');
      onFieldValueSet('phone', formattedValue);
    },
    [onFieldValueSet],
  );

  const handleAddressLocationChange = useCallback(
    (location: AddressComponents[]) => {
      let address = '';
      location.forEach((
        { short_name: shortName, types }: {short_name: string, types: string[]}
      ) => {
        const type: AddressMapKeyType = types[0];
        switch (type) {
          case 'street_number':
            address = `${shortName} ${address}`;
            break;
          case 'route':
            address = `${address}${shortName}`;
            break;
          default:
            break;
        }
        const mappedField = LOCATION_ADDRESS_MAP[type];
        if (mappedField) {
          onAddressAutocomplete(mappedField, shortName);
        }
      });
      onAddressAutocomplete('streetAddress', address);
      additionalStreetAddressRef.current?.focus();
    },
    [onAddressAutocomplete],
  );

  const handlePostalLocationChange = useCallback(
    (location: AddressComponents[]) => {
      let address = '';  
      location.forEach((
        { short_name: shortName, types }: {short_name: string, types: string[]}
      ) => {
        const type: AddressMapKeyType = types[0];
        switch (type) {
          case 'street_number':
            address = `${shortName} ${address}`;
            break;
          case 'route':
            address = `${address}${shortName}`;
            break;
          default:
            break;
        }
        const mappedField = LOCATION_ADDRESS_MAP[type];
        if (mappedField) {
          onAddressAutocomplete(mappedField, shortName);
        }
      });
      phoneNumberRef.current?.focus();
    },
    [onAddressAutocomplete],
  );

  return (
    <div className={classnames('layout-builder-form', styles.wrapper)} id='address-form'>
      {title && <h2 className={styles.title}>{title}</h2>}
      <div className={styles['two-columns-container']}>
        <div className={styles['column-item']}>
          <InputText
            label={formatMessage(messages.firstNameLabel)}
            id={`${prefix}-firstName`}
            name="firstName"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.firstName}
            errorMsg={
              (touched?.firstName && errors?.firstName) ? errors.firstName : undefined
            }
            isValid={!!touched?.firstName && !errors?.firstName || !touched?.firstName}
            dataTestId={`${prefix}-firstName`}
          />
        </div>
        <div className={styles['column-item']}>
          <InputText
            label={formatMessage(messages.lastNameLabel)}
            id={`${prefix}-lastName`}
            name="lastName"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.lastName}
            errorMsg={
              (touched?.lastName && errors?.lastName) ? errors.lastName : undefined
            }
            isValid={!!touched?.lastName && !errors?.lastName || !touched?.lastName}
          />
        </div>
      </div>
      {isAddressAutocomplete ? (
        <SearchLocationInput
          label={formatMessage(messages.streetAddressLabel)}
          id={`${prefix}-streetAddress`}
          name="streetAddress"
          onSelectLocation={handleAddressLocationChange}
          onChange={handleChange}
          onBlur={handleBlur}
          searchType="address"
          value={values.streetAddress}
          errorMsg={
            (touched?.streetAddress && errors?.streetAddress) ? errors.streetAddress : undefined
          }
          isValid={!!touched?.streetAddress && !errors?.streetAddress || !touched?.streetAddress}
        />
      ) : (
        <InputText
          label={formatMessage(messages.streetAddressLabel)}
          id={`${prefix}-streetAddress`}
          name="streetAddress"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.streetAddress}
          errorMsg={
            (touched?.streetAddress && errors?.streetAddress) ? errors.streetAddress : undefined
          }
          isValid={!!touched?.streetAddress && !errors?.streetAddress || !touched?.streetAddress}
        />
      )}
      <InputText
        ref={additionalStreetAddressRef}
        label={formatMessage(messages.apartmentLabel)}
        id={`${prefix}-additionalStreetInfo`}
        name="additionalStreetInfo"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.additionalStreetInfo}
        errorMsg={
          (touched.additionalStreetInfo && errors.additionalStreetInfo) ?
            errors.additionalStreetInfo : undefined
        }
        isValid={
          !!touched?.additionalStreetInfo &&
          !errors?.additionalStreetInfo ||
          !touched?.additionalStreetInfo
        }
      />
      <InputText
        label={formatMessage(messages.cityLabel)}
        id={`${prefix}-city`}
        name="city"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.city}
        errorMsg={(touched?.city && errors?.city) ? errors.city : undefined}
        isValid={!!touched?.city && !errors?.city || !touched?.city}
      />
      <div className={styles['two-columns-container']}>
        <div className={styles['column-item']}>
          <SelectInput
            label={formatMessage(messages.stateLabel)}
            id={`${prefix}-state`}
            name="state"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.state}
            options={statesOptions}
            errorMsg={(touched?.state && errors?.state) ? errors.state : undefined}
            isValid={!!touched?.state && !errors?.state || !touched?.state}
          />
        </div>
        <div className={styles['column-item']}>
          <SearchLocationInput
            label={formatMessage(messages.zipCodeLabel)}
            searchType="postal_code"
            id={`${prefix}-postalCode`}
            name="postalCode"
            onSelectLocation={handlePostalLocationChange}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.postalCode}
            errorMsg={(touched?.postalCode && errors?.postalCode) ? errors.postalCode : undefined}
            isValid={!!touched?.postalCode && !errors?.postalCode || !touched?.postalCode}
          />
          <InputText
            type="hidden"
            id={`${prefix}-country`}
            name="country"
            value={values.country}
            tabIndex="-1"
            isValid
            onChange={() => null}
            onBlur={() => null}
          />
        </div>
       </div>
      {isShowingContactDetails && (
        <>
          <InputText
            ref={phoneNumberRef}
            id={`${prefix}-phone`}
            name="phone"
            label={formatMessage(messages.phoneLabel)}
            type="tel"
            value={values.phone}
            onChange={formatPhoneNumber}
            onBlur={handleBlur}
            errorMsg={(touched.phone && errors.phone) ? errors.phone : undefined}
            isValid={!!touched.phone && !errors.phone || !touched.phone}
          />
          {showShopPayEmailCapture ? 
            createPortal(
              <>
                <InputText
                  id={`${prefix}-email`}
                  name="email"
                  label={formatMessage(messages.emailLabel)}
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMsg={(touched.email && errors.email) ? errors.email : undefined}
                  isValid={!!touched.email && !errors.email || !touched.email}
                  type={inputType}
                />
                <ShopPayEmailField
                  textFieldId="shipping-email"
                  containerId="address-form"
                />
              </>,
              document.getElementById(shopPayEmailFieldsPortalElementId) as HTMLElement
            )
          :
          <InputText
            id={`${prefix}-email`}
            name="email"
            label={formatMessage(messages.emailLabel)}
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMsg={(touched.email && errors.email) ? errors.email : undefined}
            isValid={!!touched.email && !errors.email || !touched.email}
            type={inputType}
          />}
        </>
      )}
    </div>
  );
};

export default AddressForm;
